import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Loader2, Scissors, ChevronLeft, Search, Play, Pause, Check, Share2,
  ChevronDown, ChevronUp, Sparkles,
} from "lucide-react";
import { ClipDetailView, RedactionModeSelect, type Clip, type RedactionMode } from "./CallsApp";
import { useAuth } from "../hooks/useAuth";
import { maskContactName } from "../lib/utils";

// ── Single-clip viewer (?clip=<id>) ─────────────────────────────────────────
export function PublicClipPage() {
  const id = useMemo(
    () => new URLSearchParams(window.location.search).get("clip") ?? "",
    [],
  );
  const { data, isLoading, error } = useQuery({
    queryKey: ["public-clip", id],
    queryFn: async (): Promise<Clip> => {
      const res = await fetch(`/api/clips/${encodeURIComponent(id)}`);
      if (!res.ok) throw new Error("Clip not found");
      return res.json();
    },
    enabled: !!id,
  });

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#fff7f3] via-[#fefbf9] to-[#f3f6ff]">
      <ClipsBrand subtitle="A shared moment from a Felix call" />
      <div className="flex-1 flex items-start justify-center px-4 py-10">
        {isLoading ? (
          <div className="py-24 grid place-items-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : error || !data ? (
          <div className="py-24 text-center text-slate-500">
            <Scissors className="w-7 h-7 mx-auto mb-3 opacity-40" />
            <div className="font-medium text-slate-700">This clip isn't available.</div>
            <div className="text-sm mt-1">It may have been removed.</div>
          </div>
        ) : (
          <PublicClipHero clip={data} />
        )}
      </div>
    </div>
  );
}

// ── Hero card for a single shared clip ──────────────────────────────────────
function PublicClipHero({ clip }: { clip: Clip }) {
  const dur = Math.max(0.1, clip.end_sec - clip.start_sec);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(clip.start_sec);
  const [copied, setCopied] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  // Clip creators (signed in via SSO on the same domain) can change the clip's
  // privacy mode straight from the public share page — so a clip that can't be
  // auto-redacted (no transcript timing) can be flipped to "Show names" and
  // played by everyone the link is shared with. Viewers stay anonymous.
  const { canCreateClips } = useAuth();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<RedactionMode>(clip.redaction_mode ?? "silence");
  const [modeSaving, setModeSaving] = useState(false);
  const [audioErr, setAudioErr] = useState<string | null>(null);
  useEffect(() => { setMode(clip.redaction_mode ?? "silence"); }, [clip.id, clip.redaction_mode]);

  // Persist the new mode, then refetch the clip so the transcript re-renders
  // with/without names. The audio <src> carries ?m=<mode> so the browser
  // refetches the correctly-redacted (or original) audio when the mode flips.
  async function changeMode(next: RedactionMode) {
    if (next === mode || modeSaving) return;
    const prev = mode;
    setMode(next); setAudioErr(null); setModeSaving(true);
    audioRef.current?.pause();
    try {
      const res = await fetch(`/api/clips/${clip.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ redaction_mode: next }),
      });
      if (!res.ok) throw new Error((await res.text()).slice(0, 200) || `${res.status}`);
      await queryClient.invalidateQueries({ queryKey: ["public-clip", clip.id] });
    } catch (e) {
      setMode(prev); // rollback
      setAudioErr(e instanceof Error ? e.message : "Couldn't update privacy");
    } finally {
      setModeSaving(false);
    }
  }

  useEffect(() => {
    const a = audioRef.current; if (!a) return;
    a.currentTime = clip.start_sec;
    const onTime = () => {
      setCurrentTime(a.currentTime);
      if (a.currentTime >= clip.end_sec) { a.pause(); a.currentTime = clip.end_sec; }
    };
    const onPlay = () => {
      if (a.currentTime < clip.start_sec || a.currentTime >= clip.end_sec) a.currentTime = clip.start_sec;
      setPlaying(true);
    };
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
    };
  }, [clip.id, clip.start_sec, clip.end_sec]);

  function togglePlay() {
    const a = audioRef.current; if (!a) return;
    if (a.paused) a.play().catch(() => { /* */ });
    else a.pause();
  }
  function seekFromBar(e: React.MouseEvent<HTMLDivElement>) {
    const a = audioRef.current; if (!a) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = clip.start_sec + pct * dur;
  }
  async function share() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* */ }
  }

  const elapsed = Math.max(0, currentTime - clip.start_sec);
  const pct = Math.min(100, (elapsed / dur) * 100);

  return (
    <div className="w-full max-w-2xl">
      {/* AI Sparkles eyebrow */}
      <div className="text-center text-[11px] uppercase tracking-[0.15em] text-[#8d6ae6] font-medium mb-3 inline-flex items-center gap-1.5 mx-auto justify-center w-full">
        <Sparkles className="w-3 h-3" /> Felix Clip
      </div>

      {/* Result badge */}
      {clip.result && (
        <div className="text-center mb-3">
          <span className="inline-flex items-center gap-1 text-[12px] font-medium px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            <Check className="w-3.5 h-3.5" /> {clip.result}
          </span>
        </div>
      )}

      {/* Title */}
      <h1 className="font-serif text-[28px] md:text-[34px] leading-[1.15] text-center text-slate-900 max-w-[26ch] mx-auto">
        {clip.title || "A Felix moment"}
      </h1>

      {/* Subtitle — only rendered if anything's left after anonymization.
          On the public endpoints the server strips contact_name and
          business_name entirely, so for shared clips this collapses out. */}
      {(clip.contact_name || clip.business_name) && (
        <div className="text-center text-[13px] text-slate-500 mt-2.5">
          {[maskContactName(clip.contact_name), clip.business_name].filter(Boolean).join(" · ")}
        </div>
      )}

      {/* Player card */}
      <div className="mt-8 bg-white rounded-3xl border border-slate-200 shadow-[0_6px_30px_-12px_rgba(15,23,42,0.15)] p-6">
        <div className="grid place-items-center">
          <button
            type="button"
            onClick={togglePlay}
            className="w-16 h-16 rounded-full bg-[#F5735A] text-white grid place-items-center hover:opacity-90 shadow-lg transition"
          >
            {playing ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-0.5" />}
          </button>
        </div>
        <div className="mt-5 flex justify-between text-xs font-mono">
          <span className="text-[#F5735A] font-semibold">{formatTime(elapsed)}</span>
          <span className="text-slate-500">{formatTime(dur)}</span>
        </div>
        <div className="h-[6px] mt-1.5 bg-[#F5735A]/15 rounded-full cursor-pointer relative" onClick={seekFromBar}>
          <div className="absolute inset-y-0 left-0 bg-[#F5735A] rounded-full" style={{ width: `${pct}%` }} />
          <div className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white border-2 border-[#F5735A] shadow-sm" style={{ left: `calc(${pct}% - 8px)` }} />
        </div>
        <audio
          ref={audioRef}
          src={`/api/clips/${clip.id}/audio?m=${mode}`}
          preload="metadata"
          onError={() => {
            if (mode !== "off") {
              setAudioErr(
                canCreateClips
                  ? "Redacted audio can't be generated for this call (no transcript timing). Switch to “Show names” below to let everyone play the original."
                  : "Audio isn't available for this clip.",
              );
            }
          }}
        />

        {/* Creator-only privacy control: flip the mode for everyone, right here. */}
        {canCreateClips && (
          <div className="mt-5">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Privacy</label>
              {modeSaving && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
            </div>
            <RedactionModeSelect value={mode} onChange={changeMode} disabled={modeSaving} />
            <div className="mt-1.5 text-[11px] text-slate-400">Applies to this clip everywhere it's shared — change anytime.</div>
          </div>
        )}

        {audioErr && (
          <div className="mt-4 text-[12px] text-rose-600 text-center max-w-[46ch] mx-auto">{audioErr}</div>
        )}

        {clip.note && (
          <p className="mt-5 text-[14px] text-slate-700 leading-relaxed text-center max-w-[52ch] mx-auto">
            {clip.note}
          </p>
        )}

        <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-center">
          <button
            type="button"
            onClick={share}
            className="h-9 px-4 rounded-lg border border-slate-200 text-[13px] text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
          >
            {copied ? <><Check className="w-3.5 h-3.5 text-emerald-500" /> Copied</> : <><Share2 className="w-3.5 h-3.5" /> Copy share link</>}
          </button>
        </div>
      </div>

      {/* Transcript collapse */}
      {clip.transcript_excerpt && (
        <div className="mt-5 bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <button
            type="button"
            onClick={() => setTranscriptOpen((o) => !o)}
            className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50"
          >
            <div className="text-[12px] font-medium text-slate-700">Transcript</div>
            <span className="text-[12px] text-slate-500 inline-flex items-center gap-1">
              {transcriptOpen ? "Hide" : "Show"}
              {transcriptOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </span>
          </button>
          {transcriptOpen && (
            <div className="px-5 pb-5 text-[14px] text-slate-700 leading-relaxed whitespace-pre-wrap">
              {clip.transcript_excerpt}
            </div>
          )}
        </div>
      )}

      {mode !== "off" && (
        <div className="mt-6 text-center text-[11px] text-slate-400">
          Names anonymized for privacy.
        </div>
      )}
    </div>
  );
}

function formatTime(sec: number): string {
  if (!isFinite(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// ── Public clips list (?tab=clips) ──────────────────────────────────────────
export function PublicClipsListPage() {
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: clips = [], isLoading } = useQuery({
    queryKey: ["public-clips"],
    queryFn: async (): Promise<Clip[]> => {
      const res = await fetch("/api/clips/public");
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clips;
    return clips.filter((c) =>
      [c.title, c.contact_name, c.business_name, c.transcript_excerpt, c.note]
        .filter(Boolean).some((s) => String(s).toLowerCase().includes(q)));
  }, [clips, query]);

  const selected = useMemo(
    () => filtered.find((c) => c.id === selectedId) ?? filtered[0] ?? null,
    [filtered, selectedId],
  );

  return (
    <div className="min-h-screen flex flex-col bg-[#fefbf9]">
      <ClipsBrand subtitle="Memorable moments from Felix calls" />
      <div className="flex-1 max-w-6xl w-full mx-auto px-4 py-6 grid grid-cols-1 md:grid-cols-[300px_1fr] gap-4">
        <aside className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[80vh]">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 shrink-0">
            <Scissors className="w-4 h-4 text-[#F5735A]" />
            <h2 className="text-sm font-bold text-slate-900">Clips</h2>
            <span className="ml-auto text-xs text-slate-500">{clips.length}</span>
          </div>
          <div className="px-3 py-2 border-b border-slate-200 shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search clips"
                className="w-full h-8 pl-8 pr-3 text-[13px] rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#F5735A]/30"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-8 grid place-items-center text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-slate-400">
                No clips yet.
              </div>
            ) : (
              <ul className="divide-y divide-slate-200">
                {filtered.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(c.id)}
                      className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-slate-50 transition ${
                        (selected?.id ?? "") === c.id ? "bg-slate-100 border-l-2 border-[#F5735A]" : ""
                      }`}
                    >
                      <div className="w-8 h-8 rounded-md bg-[#F5735A]/10 text-[#F5735A] grid place-items-center shrink-0">
                        <Scissors className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">
                          {c.title || maskContactName(c.contact_name) || "Untitled clip"}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {maskContactName(c.contact_name) || c.business_name || "—"}
                        </p>
                        {c.result && (
                          <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                            {c.result}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
        <main className="bg-white rounded-2xl border border-slate-200 overflow-hidden max-h-[80vh]">
          {selected ? (
            <ClipDetailView clip={selected} canDelete={false} />
          ) : (
            <div className="h-full grid place-items-center text-slate-400 gap-2 py-24">
              <Scissors className="w-8 h-8 opacity-40" />
              <p className="text-sm">Select a clip</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function ClipsBrand({ subtitle }: { subtitle: string }) {
  return (
    <header className="bg-white border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-4 h-[56px] flex items-center gap-3">
        <a href="/" className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-slate-900">
          <ChevronLeft className="w-3.5 h-3.5" /> Felix Wins
        </a>
        <div className="w-px h-4 bg-slate-200" />
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-[#F5735A] text-white grid place-items-center">
            <Scissors className="w-3.5 h-3.5" />
          </div>
          <div className="leading-[1.15]">
            <div className="text-[15px] font-medium text-slate-900">Felix Clips</div>
            <div className="text-[11px] text-slate-500">{subtitle}</div>
          </div>
        </div>
      </div>
    </header>
  );
}
